import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';
import { fetchDefaultAcademicYear, computedAcademicYearFallback } from '../../utils/academicYear';

const AdmissionForm = () => {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Seeded with the computed fallback for the very first paint; replaced by
  // the school's configured Settings → Academic Year as soon as it loads
  // (see the fetchDefaultAcademicYear() effect below).
  const [defaultAcademicYear, setDefaultAcademicYear] = useState(computedAcademicYearFallback());
  const [formData, setFormData] = useState({
    academicYear: defaultAcademicYear,
    startDate: '',
    endDate: '',
    maximumApplications: -1,
    description: '',
    notes: '',
  });
  const [fieldsModal, setFieldsModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formFields, setFormFields] = useState({});
  const [linkCopied, setLinkCopied] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // The public application page is a single fixed route — it resolves
  // whichever admission form is currently open itself — so there's one
  // link to share, not one per academic year.
  const publicApplyLink = `${window.location.origin}/admission-apply`;

  const copyPublicLink = () => {
    navigator.clipboard?.writeText(publicApplyLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  useEffect(() => {
    fetchAdmissionForms();
    fetchDefaultAcademicYear().then(setDefaultAcademicYear);
  }, []);

  const fetchAdmissionForms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/admissions`, { headers });
      setForms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching admission forms:', error);
      setError('Failed to fetch admission forms');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maximumApplications' ? (value === '' ? -1 : parseInt(value)) : value
    }));
  };

  const handleFieldChange = (fieldName) => {
    setFormFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleSaveForm = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.academicYear || !formData.startDate || !formData.endDate) {
      setError('Academic year, start date, and end date are required');
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/admissions/setup`, formData, { headers });
      setSuccess(response.data.message);
      setShowModal(false);
      resetForm();
      fetchAdmissionForms();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save admission form');
    }
  };

  const handleReleaseForm = async (academicYear) => {
    if (!window.confirm(`Release admission form for ${academicYear}?`)) return;

    try {
      const response = await axios.post(`${API_BASE}/admissions/${academicYear}/release`, {}, { headers });
      setSuccess(response.data.message);
      fetchAdmissionForms();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to release admission form');
    }
  };

  const handleCloseForm = async (academicYear) => {
    if (!window.confirm(`Close admission form for ${academicYear}?`)) return;

    try {
      const response = await axios.post(`${API_BASE}/admissions/${academicYear}/close`, {}, { headers });
      setSuccess(response.data.message);
      fetchAdmissionForms();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close admission form');
    }
  };

  const handleEditFields = (form) => {
    setSelectedForm(form);
    setFormFields(form.formFields);
    setFieldsModal(true);
  };

  const handleSaveFields = async () => {
    if (!selectedForm) return;

    try {
      const response = await axios.put(
        `${API_BASE}/admissions/${selectedForm.academicYear}/fields`,
        { formFields },
        { headers }
      );
      setSuccess(response.data.message);
      setFieldsModal(false);
      fetchAdmissionForms();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update form fields');
    }
  };

  const resetForm = () => {
    setFormData({
      academicYear: defaultAcademicYear,
      startDate: '',
      endDate: '',
      maximumApplications: -1,
      description: '',
      notes: '',
    });
    setEditingYear(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">✓ Active</span>;
      case 'coming-soon':
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">📅 Coming Soon</span>;
      case 'closed':
        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">✗ Closed</span>;
      case 'full':
        return <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">⊘ Full</span>;
      case 'not-released':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">🔒 Not Released</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">Unknown</span>;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">📝 Admission Form Management</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-6xl mx-auto">
            {/* Success Alert */}
            {success && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                ✓ {success}
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                ✗ {error}
              </div>
            )}

            {/* Public Application Link */}
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-indigo-800 shrink-0">🔗 Public Application Link:</span>
              <code className="flex-1 min-w-0 truncate text-sm text-indigo-700 bg-white px-3 py-1.5 rounded border border-indigo-200">
                {publicApplyLink}
              </code>
              <button
                onClick={copyPublicLink}
                className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium shrink-0"
              >
                {linkCopied ? '✓ Copied' : 'Copy Link'}
              </button>
              <p className="w-full text-xs text-indigo-600">Share this link with parents so they can apply — it always shows whichever form is currently open.</p>
            </div>

            {/* Header with Add Button */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">📋 Admission Forms</h2>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
              >
                + New Form
              </button>
            </div>

            {/* Forms List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin">
                  <div className="h-12 w-12 border-4 border-indigo-600 border-r-transparent rounded-full"></div>
                </div>
                <p className="mt-4 text-gray-600">Loading admission forms...</p>
              </div>
            ) : forms.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-lg text-gray-500">No admission forms found</p>
                <p className="text-sm text-gray-400">Create a new admission form to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {forms.map((form) => (
                  <div key={form._id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-800">{form.academicYear}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(form.startDate).toLocaleDateString('en-IN')} - {new Date(form.endDate).toLocaleDateString('en-IN')}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(form.status)}
                    </div>

                    {form.description && (
                      <p className="text-gray-700 mb-4">{form.description}</p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Applications</p>
                        <p className="text-xl font-bold text-gray-800">
                          {form.currentApplicationCount}{form.maximumApplications !== -1 ? `/${form.maximumApplications}` : '+'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Status</p>
                        <p className="text-sm font-semibold text-gray-800">{form.isOpen ? 'Open' : 'Closed'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Days Remaining</p>
                        <p className="text-xl font-bold text-gray-800">{form.daysRemaining}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Released By</p>
                        <p className="text-sm font-semibold text-gray-800">{form.releasedBy?.name || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {!form.isOpen && form.status !== 'closed' && (
                        <button
                          onClick={() => handleReleaseForm(form.academicYear)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                        >
                          🚀 Release
                        </button>
                      )}
                      {form.isOpen && (
                        <button
                          onClick={() => handleCloseForm(form.academicYear)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                        >
                          ✓ Close
                        </button>
                      )}
                      <button
                        onClick={() => handleEditFields(form)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                      >
                        ⚙️ Fields
                      </button>
                      <button
                        onClick={() => {
                          setFormData({
                            academicYear: form.academicYear,
                            startDate: form.startDate.split('T')[0],
                            endDate: form.endDate.split('T')[0],
                            maximumApplications: form.maximumApplications,
                            description: form.description,
                            notes: form.notes,
                          });
                          setEditingYear(form.academicYear);
                          setShowModal(true);
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium"
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingYear ? '✏️ Edit Admission Form' : '📝 Create New Admission Form'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white text-2xl hover:text-indigo-100"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveForm} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Academic Year *
                </label>
                <input
                  type="text"
                  name="academicYear"
                  value={formData.academicYear}
                  onChange={handleChange}
                  disabled={!!editingYear}
                  placeholder="2024-2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:bg-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Applications (-1 = Unlimited)
                </label>
                <input
                  type="number"
                  name="maximumApplications"
                  value={formData.maximumApplications}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Add description for this admission form..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any internal notes..."
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  {editingYear ? 'Update Form' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fields Modal */}
      {fieldsModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">⚙️ Form Fields</h2>
              <button
                onClick={() => setFieldsModal(false)}
                className="text-white text-2xl hover:text-indigo-100"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-3">
              {Object.entries(formFields).map(([fieldName, isEnabled]) => (
                <label key={fieldName} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleFieldChange(fieldName)}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="ml-3 text-gray-700 font-medium capitalize">
                    {fieldName.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </label>
              ))}

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  onClick={() => setFieldsModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFields}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  Save Fields
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionForm;
